import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { MapPin, Loader2, Radius } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tool: bulk-widen every active quote request's search_radius
 * to 25 km, then re-run the shop notification pipeline so any newly-
 * in-range shops get emailed. Idempotent — requests already ≥25 km
 * are skipped.
 */
export function AdminBulkRadiusResetPanel({ onCompleted }) {
  const { token } = useAuth();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Widen ALL active quote requests to 25 km and email newly-in-range shops? " +
      "This will not re-email shops that were already notified.",
    )) return;
    setRunning(true);
    try {
      const res = await axios.post(
        `${API}/admin/quote-requests/reset-radius-to-25`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5 * 60 * 1000,
        },
      );
      setLastResult(res.data);
      toast.success(
        `Widened ${res.data.total_widened} of ${res.data.total_examined} · emailed ${res.data.total_newly_notified} new shops`,
      );
      if (onCompleted) onCompleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk radius reset failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="glass-card border-blue-300 dark:border-blue-800">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Radius className="h-4 w-4 text-blue-600" />
          Widen active requests to 25 km
        </CardTitle>
        <CardDescription className="text-xs">
          Some older requests were created with a 20 km default. This
          bulk-updates every currently-active request to 25 km and emails
          any shops newly in range. Idempotent — safe to re-run.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={run}
            disabled={running}
            className="bg-blue-600 text-white hover:bg-blue-700"
            data-testid="widen-radius-btn"
          >
            {running ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <MapPin className="h-3.5 w-3.5 mr-1.5" />
            )}
            {running ? "Widening…" : "Widen all active requests to 25 km"}
          </Button>
          {lastResult && (
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">
                {lastResult.total_widened} widened
              </Badge>
              <Badge className="text-[10px] bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/40">
                {lastResult.total_newly_notified} shops emailed
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {lastResult.total_examined} examined
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Admin dialog: restart a single quote request. Extends expiry
 * by N hours from now, flips status back to active if needed,
 * optionally widens the radius, and re-emails the eligible shop
 * set. `include_previously_notified` re-emails everyone (useful
 * when you want to bump forgotten shops).
 */
export function AdminRestartRequestDialog({ open, onClose, request, onCompleted }) {
  const { token } = useAuth();
  const [hours, setHours] = useState(48);
  const [radius, setRadius] = useState(25);
  const [includeAll, setIncludeAll] = useState(false);
  const [running, setRunning] = useState(false);

  if (!open || !request) return null;

  const submit = async () => {
    setRunning(true);
    try {
      const body = {
        hours_from_now: Number(hours),
        include_previously_notified: includeAll,
      };
      if (Number(radius)) body.search_radius = Number(radius);
      const res = await axios.post(
        `${API}/admin/quote-requests/${request.request_id}/restart`,
        body,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
      );
      toast.success(
        `Restarted · ${res.data.newly_notified_count} new shops emailed (${res.data.total_notified_after} total)`,
      );
      onClose();
      if (onCompleted) onCompleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restart failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="restart-request-dialog"
    >
      <div className="w-full max-w-md rounded-xl bg-card shadow-2xl">
        <div className="px-5 py-4 border-b">
          <h3 className="font-heading text-lg font-semibold">
            Restart request {request.request_number || `#${request.request_id}`}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Extends expiry, flips status back to active, and re-emails shops.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium">Extend expiry by (hours)</label>
            <input
              type="number"
              min="1"
              max="336"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border bg-background text-sm"
              data-testid="restart-hours-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              1 – 336 (14 days). Common: 24, 48, 72.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">Search radius (km)</label>
            <input
              type="number"
              min="1"
              max="200"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border bg-background text-sm"
              data-testid="restart-radius-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Current: {request.search_radius || 25} km. Widening here will pull
              in additional shops.
            </p>
          </div>
          <label
            className="flex items-start gap-2 cursor-pointer text-sm"
            data-testid="restart-include-all-label"
          >
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              className="mt-0.5"
              data-testid="restart-include-all-checkbox"
            />
            <span>
              Also re-email shops that were previously notified
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                Default off — only newly-in-range shops receive an email.
                Turn on if you want to bump everyone.
              </span>
            </span>
          </label>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={running}
            data-testid="restart-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={running}
            className="bg-primary text-primary-foreground"
            data-testid="restart-confirm-btn"
          >
            {running ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            {running ? "Restarting…" : "Restart & email shops"}
          </Button>
        </div>
      </div>
    </div>
  );
}
